import { useEffect, useRef } from 'react';

const SPECIALTIES = [
  { icon: 'ri-eye-line', label: '웨딩 메이크업', sub: 'Bridal Makeup' },
  { icon: 'ri-camera-line', label: '화보 & 촬영', sub: 'Editorial & Shoot' },
  { icon: 'ri-star-line', label: '무대 & 행사', sub: 'Stage & Event' },
  { icon: 'ri-palette-line', label: '트렌드 룩', sub: 'Trend Styling' },
  { icon: 'ri-leaf-line', label: '네추럴 메이크업', sub: 'Natural Look' },
  { icon: 'ri-shield-check-line', label: '피부 커버', sub: 'Skin Coverage' },
];

const CAREER = [
  '전 청담 S 웨딩 수석 아티스트 역임',
  '한국 메이크업 아티스트 협회 정회원',
  '뷰티 TV 방송 메이크업 담당 출연',
  '2022 코리아 뷰티 어워즈 수상',
  '누적 고객 2,000명 이상 시술 경험',
];

export default function MakeupSection() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    const els = ref.current?.querySelectorAll('.section-reveal, .section-reveal-left, .section-reveal-right, .section-reveal-scale');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="makeup" ref={ref} className="bg-cream py-20 px-7 md:py-28 md:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14 md:mb-20 section-reveal">
          <span className="inline-block text-gold font-sans text-xs sm:text-sm font-semibold tracking-widest uppercase mb-3">
            Professional Makeup · 专业彩妆 · プロメイクアップ
          </span>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-gray-900 font-bold mb-4">메이크업</h2>
          <div className="w-16 h-0.5 bg-gold mx-auto mb-5"></div>
          <p className="font-sans text-gray-500 max-w-xl mx-auto text-sm sm:text-base">
            전문 아티스트의 손길로 당신만의 아름다움을 완성합니다. 트렌드와 개성을 동시에 담아내는 헤라만의 메이크업.
          </p>
        </div>

        {/* Artist Profile */}
        <div className="grid lg:grid-cols-2 gap-14 md:gap-18 items-start">
          {/* Photo */}
          <div className="section-reveal-left">
            <div className="relative rounded-2xl overflow-hidden h-80 sm:h-96 md:h-[540px]">
              <img
                src="https://public.readdy.ai/ai/img_res/479bb6ec7569b865065662ce41239586.jpg"
                alt="헤라 메이크업 아티스트"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
              {/* Name Badge */}
              <div className="absolute bottom-5 left-5 right-5">
                <div className="bg-white/95 backdrop-blur-sm border border-gold/30 rounded-xl px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-sans text-xs text-gold-dark font-medium">Head Makeup Artist · 首席化妆师</p>
                    </div>
                    <div className="w-10 h-10 flex items-center justify-center bg-gold rounded-full text-white">
                      <i className="ri-award-line text-base"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Career Highlights */}
            <div className="mt-5 bg-white border border-gold/15 rounded-2xl p-5 section-reveal">
              <p className="font-sans text-xs text-gold-dark font-semibold tracking-widest uppercase mb-3">Career Highlights</p>
              <ul className="space-y-2">
                {CAREER.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-line text-gold text-sm"></i>
                    </span>
                    <span className="font-sans text-xs sm:text-sm text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right Content */}
          <div className="section-reveal-right">
            {/* Bio Card */}
            <div className="bg-white border border-gold/20 rounded-2xl p-6 md:p-8 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-gold/10 rounded-full border border-gold/30">
                  <i className="ri-user-star-line text-gold text-lg"></i>
                </div>
                <div>
                  <p className="font-serif text-base font-bold text-gray-900">아티스트 소개</p>
                  <p className="font-sans text-xs text-gray-400">Artist Profile</p>
                </div>
              </div>
              <p className="font-sans text-sm text-gray-600 leading-relaxed">
                10년 이상의 현장 경력을 바탕으로 웨딩, 화보, 방송, 무대 등 다양한 분야에서 활약해온 헤드 메이크업 아티스트입니다.
                고객 한 분 한 분의 피부톤과 얼굴형을 분석하여 가장 자연스럽고 완벽한 룩을 제안합니다.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="bg-gold/10 text-gold-dark text-xs font-medium px-3 py-1 rounded-full font-sans">경력 10년+</span>
                <span className="bg-plum/10 text-plum text-xs font-medium px-3 py-1 rounded-full font-sans">공인 아티스트</span>
                <span className="bg-gold/10 text-gold-dark text-xs font-medium px-3 py-1 rounded-full font-sans">어워즈 수상</span>
              </div>
            </div>

            {/* Specialties */}
            <div className="mb-6">
              <p className="font-sans text-xs text-gold-dark font-semibold tracking-widest uppercase mb-4">Specialties · 专长 · 専門分野</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {SPECIALTIES.map((s, i) => (
                  <div
                    key={s.label}
                    className="section-reveal-scale bg-white border border-gold/15 rounded-xl p-3.5 hover:border-gold/40 hover:-translate-y-1 transition-all duration-300 cursor-default"
                    style={{ transitionDelay: `${0.06 + i * 0.08}s` }}
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-gold/10 rounded-lg mb-2">
                      <i className={`${s.icon} text-gold text-sm`}></i>
                    </div>
                    <p className="font-serif text-xs font-bold text-gray-900 mb-0.5">{s.label}</p>
                    <p className="font-sans text-xs text-gray-400">{s.sub}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-plum to-plum/80 rounded-2xl p-5 text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="font-sans text-sm text-white/70 mb-0.5">메이크업 예약 · Makeup Reservation</p>
                <p className="font-serif text-xl font-bold">전문 아티스트와 상담하세요</p>
                <p className="font-sans text-xs text-white/60 mt-1">개인 맞춤 스타일링 · Personalized Styling</p>
              </div>
              <button
                onClick={() => { const el = document.querySelector('#reservation'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
                className="flex-shrink-0 bg-gold text-white px-5 py-2.5 rounded-full text-sm font-sans font-semibold whitespace-nowrap cursor-pointer hover:bg-gold-dark transition-colors duration-200"
              >
                예약하기
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
